const ACCENT = '#2020a0';
const INK = '#1a1a2e';
const MUTED = '#5c5c6e';
const FAINT = '#aaaab2';
const LINE = '#e4e4ed';
const GREEN = '#248c45';

const SOCIAL_ICONS = {
  facebook: 'facebook',
  instagram: 'instagram',
  tiktok: 'tiktok',
  x: 'x',
  twitter: 'x',
  pinterest: 'pinterest',
  linkedin: 'linkedin',
};

function filled(value) {
  return Boolean(value && String(value).trim());
}

function metricHeadline(metric, lang) {
  const unit = String(metric?.unit || '').trim();
  const raw = String(metric?.value ?? '').trim();
  const numeric =
    typeof metric?.value === 'number' && Number.isFinite(metric.value)
      ? metric.value
      : /^\d+(?:[.,]\d+)?$/.test(raw)
        ? Number(raw.replace(',', '.'))
        : null;
  if (numeric == null) return [raw, unit].filter(Boolean).join(' ');
  const formatted = new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'es-ES', {
    maximumFractionDigits: 2,
  }).format(numeric);
  return unit ? `${formatted} ${unit}` : formatted;
}

export function sectionModel(kit, langKey) {
  const lang = kit?.[langKey] || {};
  const shared = kit?.shared || {};
  const metrics = (lang.audience?.metrics || []).map((metric, index) => ({
    ...metric,
    value: shared.metrics?.[index]?.value ?? metric.value,
  }));
  const countries = (lang.profile?.countries || []).map((row, index) => ({
    ...row,
    percent: shared.countries?.[index]?.percent ?? row.percent ?? 0,
  }));
  const cities = (lang.profile?.cities || []).map((row, index) => ({
    ...row,
    percent: shared.cities?.[index]?.percent ?? row.percent ?? 0,
  }));
  const formats = (lang.formats?.items || []).map((item, index) => ({
    ...item,
    muted: Boolean(shared.formats?.[index]?.muted),
  }));
  const packages = (lang.packages?.items || []).map((plan, index) => ({
    ...plan,
    price: shared.packages?.[index]?.price ?? plan.price,
    featured: Boolean(shared.packages?.[index]?.featured),
    cta_url: shared.packages?.[index]?.cta_url ?? plan.cta_url,
  }));
  const steps = (lang.process?.steps || []).map((step, index) => ({
    ...step,
    number: shared.steps?.[index]?.number ?? step.number,
  }));
  return {
    seo: lang.seo || {},
    hero: { ...(lang.hero || {}), pdf_url: shared.pdf_url || '' },
    audience: { ...(lang.audience || {}), metrics },
    profile: { ...(lang.profile || {}), countries, cities },
    formats: { ...(lang.formats || {}), items: formats },
    packages: { ...(lang.packages || {}), items: packages },
    process: { ...(lang.process || {}), steps },
    faqs: lang.faqs || { items: [] },
    contact: {
      ...(lang.contact || {}),
      email: shared.email || lang.contact?.email || '',
      whatsapp: shared.whatsapp || lang.contact?.whatsapp || '',
      socials: (shared.socials || lang.contact?.socials || []).map((social) => ({
        platform: social.platform,
        url: social.url,
        label: social.label,
      })),
    },
  };
}

function Eyebrow({ children }) {
  if (!filled(children)) return null;
  return (
    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '1.2px', color: ACCENT }}>
      {children}
    </p>
  );
}

function Title({ children, size = 28 }) {
  if (!filled(children)) return null;
  return (
    <h2 style={{ margin: '8px 0 0', fontSize: size, lineHeight: 1.2, fontWeight: 800, color: INK }}>
      {children}
    </h2>
  );
}

function Note({ children }) {
  if (!filled(children)) return null;
  return <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.5, color: MUTED }}>{children}</p>;
}

function Bars({ items }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {(items || []).map((item, index) => (
        <div key={`${item.name}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 130, flexShrink: 0, fontSize: 13, fontWeight: 600, color: INK }}>{item.name}</span>
          <span style={{ flex: 1, height: 8, borderRadius: 99, background: LINE, overflow: 'hidden' }}>
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${Math.max(0, Math.min(100, Number(item.percent) || 0))}%`,
                background: ACCENT,
                borderRadius: 99,
              }}
            />
          </span>
          <span style={{ width: 42, textAlign: 'right', fontSize: 12, fontWeight: 700, color: MUTED }}>
            {item.percent} %
          </span>
        </div>
      ))}
    </div>
  );
}

function PagePreview({ model }) {
  return (
    <div>
      <Eyebrow>{model.hero.eyebrow}</Eyebrow>
      <Title size={36}>{model.hero.title}</Title>
      <Note>{model.hero.description}</Note>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 18 }}>
        {filled(model.hero.download_label) ? (
          <span style={{ background: ACCENT, color: 'white', borderRadius: 12, padding: '14px 22px', fontWeight: 700 }}>
            {model.hero.download_label}
          </span>
        ) : null}
        {filled(model.hero.secondary_label) ? (
          <span style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: '14px 22px', fontWeight: 700, color: INK }}>
            {model.hero.secondary_label}
          </span>
        ) : null}
      </div>
      {filled(model.hero.updated_label) ? (
        <p style={{ marginTop: 12, fontSize: 11, color: FAINT }}>{model.hero.updated_label}</p>
      ) : null}
      <p style={{ marginTop: 18, fontSize: 12, color: MUTED }}>
        SEO: {model.seo.title || '—'}
        <br />
        {model.seo.description || ''}
      </p>
    </div>
  );
}

function AudiencePreview({ model, lang }) {
  return (
    <div>
      <Eyebrow>{model.audience.eyebrow}</Eyebrow>
      <Title>{model.audience.title}</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginTop: 20 }}>
        {(model.audience.metrics || []).map((metric, index) => (
          <article key={index} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: '20px 18px', background: 'white' }}>
            <p style={{ margin: 0, fontSize: 28, fontWeight: 800, color: INK }}>{metricHeadline(metric, lang)}</p>
            <p style={{ margin: '6px 0 0', fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', color: FAINT }}>{metric.label}</p>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: GREEN }}>{metric.note}</p>
          </article>
        ))}
      </div>
    </div>
  );
}

function ProfilePreview({ model }) {
  return (
    <div>
      <Eyebrow>{model.profile.eyebrow}</Eyebrow>
      <Title>{model.profile.title}</Title>
      <article style={{ marginTop: 20, border: `1px solid ${LINE}`, borderRadius: 12, padding: 24, background: 'white' }}>
        {filled(model.profile.countries_label) ? (
          <p style={{ margin: '0 0 12px', fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', color: FAINT }}>
            {model.profile.countries_label}
          </p>
        ) : null}
        <Bars items={model.profile.countries} />
        <div style={{ height: 1, background: LINE, margin: '18px 0' }} />
        {filled(model.profile.cities_label) ? (
          <p style={{ margin: '0 0 12px', fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', color: FAINT }}>
            {model.profile.cities_label}
          </p>
        ) : null}
        <Bars items={model.profile.cities} />
      </article>
    </div>
  );
}

function TravelerPreview({ model }) {
  const traveler = model.profile.traveler || {};
  return (
    <aside style={{ borderRadius: 12, background: '#f7f7fb', padding: 24 }}>
      <Eyebrow>{traveler.eyebrow}</Eyebrow>
      <Note>{traveler.description}</Note>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        {(traveler.attributes || []).map((attr, index) => (
          <div key={index}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: FAINT }}>{attr.label}</p>
            <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600, color: INK }}>{attr.value}</p>
          </div>
        ))}
      </div>
    </aside>
  );
}

function FormatsPreview({ model }) {
  return (
    <div>
      <Eyebrow>{model.formats.eyebrow}</Eyebrow>
      <Title>{model.formats.title}</Title>
      <Note>{model.formats.description}</Note>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginTop: 20 }}>
        {(model.formats.items || []).map((item, index) => (
          <article
            key={index}
            style={{
              border: `1px solid ${LINE}`,
              borderRadius: 12,
              padding: 22,
              background: item.muted ? '#f2f2f2' : 'white',
            }}
          >
            {filled(item.chip) ? (
              <span style={{ display: 'inline-flex', borderRadius: 99, background: '#eeeef7', color: ACCENT, fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', padding: '5px 11px' }}>
                {item.chip}
              </span>
            ) : null}
            <h3 style={{ margin: '12px 0 0', fontSize: 18, fontWeight: 800, color: INK }}>{item.title}</h3>
            {filled(item.description) ? <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.5, color: MUTED }}>{item.description}</p> : null}
            {(item.specs || []).length ? (
              <div style={{ marginTop: 14, borderTop: `1px solid ${LINE}`, paddingTop: 12 }}>
                {item.specs.map((spec, specIndex) => (
                  <div key={specIndex} style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                    <span style={{ width: 110, flexShrink: 0, fontSize: 10, fontWeight: 700, color: FAINT }}>{spec.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>{spec.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

function PackagesPreview({ model }) {
  return (
    <div>
      <Eyebrow>{model.packages.eyebrow}</Eyebrow>
      <Title>{model.packages.title}</Title>
      <Note>{model.packages.description}</Note>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 20 }}>
        {(model.packages.items || []).map((plan, index) => (
          <article
            key={index}
            style={{
              borderRadius: 12,
              padding: '24px 22px',
              background: 'white',
              border: plan.featured ? `2px solid ${ACCENT}` : `1px solid ${LINE}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {plan.featured && filled(plan.featured_label) ? (
              <span style={{ alignSelf: 'flex-start', borderRadius: 99, background: ACCENT, color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', padding: '5px 11px' }}>
                {plan.featured_label}
              </span>
            ) : null}
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '1.2px', color: plan.featured ? ACCENT : FAINT }}>{plan.name}</p>
            <p style={{ margin: 0 }}>
              <span style={{ fontSize: 30, fontWeight: 800, color: INK }}>{plan.price}</span>{' '}
              <span style={{ fontSize: 12, color: FAINT }}>{plan.period}</span>
            </p>
            {filled(plan.description) ? <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: MUTED }}>{plan.description}</p> : null}
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(plan.includes || []).map((line, lineIndex) => (
                <li key={lineIndex} style={{ fontSize: 13, color: INK }}>
                  <span style={{ color: GREEN, fontWeight: 700, marginRight: 8 }}>✓</span>
                  {line}
                </li>
              ))}
            </ul>
            {filled(plan.cta_label) ? (
              <span
                style={{
                  marginTop: 'auto',
                  textAlign: 'center',
                  borderRadius: 12,
                  padding: '12px 16px',
                  fontWeight: 700,
                  fontSize: 14,
                  background: plan.featured ? ACCENT : 'white',
                  color: plan.featured ? 'white' : INK,
                  border: plan.featured ? 'none' : `1.5px solid ${INK}`,
                }}
              >
                {plan.cta_label}
              </span>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

function ProcessPreview({ model }) {
  return (
    <div>
      <Eyebrow>{model.process.eyebrow}</Eyebrow>
      <Title>{model.process.title}</Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18, marginTop: 20 }}>
        {(model.process.steps || []).map((step, index) => (
          <div key={index}>
            <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: ACCENT }}>{step.number}</p>
            <p style={{ margin: '8px 0 0', fontSize: 15, fontWeight: 700, color: INK }}>{step.title}</p>
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: MUTED }}>{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqsPreview({ model }) {
  return (
    <div>
      <Eyebrow>{model.faqs.eyebrow}</Eyebrow>
      <Title>{model.faqs.h3}</Title>
      <Note>{model.faqs.p}</Note>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(model.faqs.items || []).filter((item) => filled(item.question)).map((item, index) => (
          <details key={index} style={{ border: `1px solid ${LINE}`, borderRadius: 12, background: 'white', padding: '12px 16px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700, color: INK }}>{item.question}</summary>
            <div
              style={{ marginTop: 10, fontSize: 14, lineHeight: 1.5, color: MUTED }}
              dangerouslySetInnerHTML={{ __html: item.answer_html_updated || item.answer_html || item.answer || '' }}
            />
          </details>
        ))}
      </div>
    </div>
  );
}

function ContactPreview({ model }) {
  const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';
  return (
    <div>
      <Eyebrow>{model.contact.eyebrow}</Eyebrow>
      {filled(model.contact.whatsapp) ? (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 14, border: '1px solid #cfeedd', background: '#f4fbf7', borderRadius: 12, padding: '14px 18px' }}>
          <span style={{ width: 40, height: 40, borderRadius: 99, background: '#25D366', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
            W
          </span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', color: '#1f8a45' }}>{model.contact.whatsapp_kicker || 'WHATSAPP'}</span>
            <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: INK }}>{model.contact.whatsapp_label}</span>
            <strong style={{ display: 'block', marginTop: 2 }}>{model.contact.whatsapp}</strong>
          </span>
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: 16 }}>
        <article style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 20, background: 'white' }}>
          <p style={{ margin: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', color: FAINT }}>{model.contact.email_kicker}</p>
          <p style={{ margin: '8px 0 0', fontSize: 15, fontWeight: 800, color: INK }}>{model.contact.email}</p>
          <p style={{ margin: '6px 0 0', fontSize: 12, color: MUTED }}>{model.contact.email_note}</p>
        </article>
        <article style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 20, background: 'white' }}>
          <p style={{ margin: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.6px', color: FAINT }}>{model.contact.social_kicker}</p>
          <p style={{ margin: '8px 0 0', fontSize: 15, fontWeight: 800, color: INK }}>{model.contact.social_handle}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {(model.contact.socials || []).filter((item) => filled(item.url)).map((social, index) => {
              const file = SOCIAL_ICONS[social.platform];
              const src = publicUrl && file ? `${publicUrl}/redesign/contact/${file}.png` : '';
              return src ? (
                <img key={index} src={src} alt={social.label || social.platform} width={28} height={28} style={{ width: 28, height: 28, borderRadius: 7 }} />
              ) : (
                <span key={index} style={{ width: 28, height: 28, borderRadius: 7, background: '#eeeef7', color: ACCENT, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>
                  {(social.label || social.platform || '?').slice(0, 1).toUpperCase()}
                </span>
              );
            })}
          </div>
        </article>
      </div>
    </div>
  );
}

const RENDERERS = {
  page: PagePreview,
  audience: AudiencePreview,
  profile: ProfilePreview,
  traveler: TravelerPreview,
  formats: FormatsPreview,
  packages: PackagesPreview,
  process: ProcessPreview,
  faqs: FaqsPreview,
  contact: ContactPreview,
};

const SECTION_ORDER = Object.keys(RENDERERS);

export default function MediaKitSectionPreview({ section, kit, lang }) {
  const model = sectionModel(kit, lang);
  const ids = section === 'all' ? SECTION_ORDER : [section];
  return (
    <div style={{ fontFamily: 'Figtree, system-ui, sans-serif', color: INK, padding: '8px 4px 24px', display: 'flex', flexDirection: 'column', gap: 40 }}>
      {ids.map((id) => {
        const Renderer = RENDERERS[id] || PagePreview;
        return <Renderer key={id} model={model} lang={lang} />;
      })}
    </div>
  );
}
